import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
/** The keyframes snippets/cart-drawer.liquid runs while the dialog carries data-entrance. */
const ENTRANCE_ANIMATIONS = new Set(['cart-row-slap', 'cart-count-pop']);

class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #entranceTimer;

  /** Bumped on every open, so a stale wait never clears a newer entrance's mark. */
  #entranceToken = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#markEntrance);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    // Deferred past DOMContentLoaded: this element's own upgrade (and this connectedCallback) can
    // run as early as parsing finishes — before snippets/theme-drawer.liquid's own inline module
    // script (which registers the `theme-drawer:open` listener that adds the squeeze class) has had
    // its turn in the deferred-script queue. `readyState` is already 'interactive' — not 'loading' —
    // throughout that whole queue, so 'complete' (set only once DOMContentLoaded has already fired)
    // is the only reliable signal that it's safe to call #themeDrawer.open() immediately; otherwise
    // wait for the event so the dispatch isn't lost on a listener that doesn't exist yet.
    if (document.readyState === 'complete') {
      this.#openFromRedirectParam();
    } else {
      document.addEventListener('DOMContentLoaded', () => this.#openFromRedirectParam(), { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#markEntrance);
    clearTimeout(this.#entranceTimer);
  }

  /**
   * Marks the dialog with data-entrance while the item stickers slap in (snippets/cart-drawer.liquid).
   * Only a real open fires theme-drawer:open: a bag restored open across a page load and the reopen on
   * a modal-breakpoint change do not, so neither replays the entrance. Once the mark is gone, a row the
   * morph moves or re-inserts (every row below a removed one) has no animation left to restart. Kept
   * off #handleDrawerOpen, which the restore path also calls.
   */
  #markEntrance = async () => {
    const dialog = this.#dialog;
    if (!dialog) return;

    const token = ++this.#entranceToken;
    const clear = () => {
      if (token !== this.#entranceToken) return;
      clearTimeout(this.#entranceTimer);
      dialog.removeAttribute('data-entrance');
    };

    dialog.setAttribute('data-entrance', '');
    // Fallback only (a hidden tab can stall animation promises); the normal clear is below.
    clearTimeout(this.#entranceTimer);
    this.#entranceTimer = setTimeout(clear, 4000);

    // Clear the mark only once every entrance animation has ended, including the slap of a row the
    // morph inserts mid-entrance. A fixed timer cut slow first renders into a snap, and lifting the
    // mark mid-slap flashed an inserted row's .adding keyframe for a frame. getAnimations() flushes
    // style, so the animations the mark just started are already listed. With reduced motion there
    // are none and the mark goes at once; closing the bag cancels them, which settles the wait too.
    // Finished ones must be skipped: fill-mode both keeps them in getAnimations() with an already
    // settled promise, and awaiting those spins this loop in microtasks and freezes the page. The
    // pass cap is a second guard against any state that settles without leaving the list.
    for (let pass = 0; pass < 8 && token === this.#entranceToken; pass++) {
      const running = dialog
        .getAnimations({ subtree: true })
        .filter(
          (animation) =>
            'animationName' in animation &&
            ENTRANCE_ANIMATIONS.has(animation.animationName) &&
            animation.playState !== 'finished'
        );
      if (!running.length) break;
      await Promise.allSettled(running.map((animation) => animation.finished));
    }
    clear();
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Opens the drawer when the page was loaded with `?cart=open` — set by assets/sticker-editor.js
   * and assets/sticker-sheet-editor.js when they redirect back to the product page after an
   * Edit/Save re-add from a design page, or as their fallback when that page had no upgraded,
   * non-empty cart drawer to open in place (a plain design-page add opens the drawer right there
   * and never reaches this). Independent of the `auto-open` attribute (which follows the
   * merchant's "auto open cart drawer" setting), so this only ever fires for that specific redirect
   * and leaves regular add-to-cart behavior on product pages untouched.
   */
  #openFromRedirectParam() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('cart') !== 'open') return;

    url.searchParams.delete('cart');
    window.history.replaceState(window.history.state, '', url);

    if (!this.#isCartEmpty()) this.#themeDrawer?.open();
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
